/* Mobile menu drawer — sub-menu panels + accordion (2026-10 v3 global).
 * Open/close of the drawer itself stays in assets/sections.js (MobileMenuDrawer: trigger,
 * [data-mobile-menu-close], overlay, body.is-active). This file only handles what happens
 * inside it (markup: snippets/mobile-menu-drawer.liquid):
 *   - "Shop by" row ([data-has-tier-2="true"]) → its panel [data-mobile-tier-2="<handle>"]
 *     slides in; the root list is made inert; focus moves to "‹ Menu" and back on return.
 *   - panel "‹ Menu" ([data-caf-drawer-back]) → back; panel × ([data-caf-drawer-close]) →
 *     clicks the header close button so sections.js runs its normal close.
 *   - accordion group heads (.caf-drawer-group__head) toggle aria-expanded + hidden.
 *   - when the drawer finishes closing, any open panel resets (no animation).
 */
(() => {
  const drawers = document.querySelectorAll('.mobile-menu-drawer');
  if (!drawers.length) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const PANEL_MS = 300;

  drawers.forEach((drawer) => {
    if (drawer.__cafDrawerInit) return;
    drawer.__cafDrawerInit = true;

    const rootList = drawer.querySelector('[data-caf-drawer-root]');
    let activePanel = null;
    let activeTrigger = null;
    let hideTimer = 0;

    const setRootInert = (on) => {
      if (!rootList) return;
      if (on) rootList.setAttribute('inert', '');
      else rootList.removeAttribute('inert');
    };

    const openPanel = (trigger) => {
      const handle = trigger.getAttribute('data-mobile-menu-title');
      const panel = handle && drawer.querySelector(`[data-mobile-tier-2="${CSS.escape(handle)}"]`);
      if (!panel) return;
      window.clearTimeout(hideTimer);
      if (activePanel && activePanel !== panel) closePanel({ focus: false, instant: true });
      activePanel = panel;
      activeTrigger = trigger;
      panel.hidden = false;
      // Next frame so the slide-in transition runs from the off-canvas position.
      window.requestAnimationFrame(() => {
        panel.classList.add('active');
        trigger.setAttribute('aria-expanded', 'true');
        drawer.classList.add('caf-drawer--panel-open');
        setRootInert(true);
        drawer.scrollTop = 0;
        const back = panel.querySelector('[data-caf-drawer-back]');
        if (back) back.focus({ preventScroll: true });
      });
    };

    const closePanel = ({ focus = true, instant = false } = {}) => {
      const panel = activePanel;
      if (!panel) return;
      const trigger = activeTrigger;
      activePanel = null;
      activeTrigger = null;
      panel.classList.remove('active');
      drawer.classList.remove('caf-drawer--panel-open');
      setRootInert(false);
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        if (focus) trigger.focus({ preventScroll: true });
      }
      const hide = () => {
        if (!panel.classList.contains('active')) panel.hidden = true;
      };
      if (instant || reduceMotion.matches) hide();
      else hideTimer = window.setTimeout(hide, PANEL_MS);
    };

    const toggleGroup = (head) => {
      const group = head.closest('.caf-drawer-group');
      const links = group && group.querySelector('.caf-drawer-group__links');
      if (!links) return;
      const open = head.getAttribute('aria-expanded') !== 'true';
      head.setAttribute('aria-expanded', open ? 'true' : 'false');
      group.classList.toggle('is-open', open);
      links.hidden = !open;
    };

    drawer.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const trigger = target.closest('[data-has-tier-2="true"]');
      if (trigger && drawer.contains(trigger)) {
        event.preventDefault();
        openPanel(trigger);
        return;
      }
      if (target.closest('[data-caf-drawer-back]')) {
        event.preventDefault();
        closePanel();
        return;
      }
      if (target.closest('[data-caf-drawer-close]')) {
        event.preventDefault();
        const close = drawer.querySelector('[data-mobile-menu-close]');
        setRootInert(false); // the header close lives in the (inert) root list
        if (close) close.click();
        return;
      }
      const head = target.closest('.caf-drawer-group__head');
      if (head) {
        event.preventDefault();
        toggleGroup(head);
      }
    });

    drawer.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && activePanel) {
        event.stopPropagation();
        closePanel();
      }
    });

    // Drawer closed (sections.js sets data-animation-state="closed" after the slide-out).
    new MutationObserver(() => {
      if (drawer.getAttribute('data-animation-state') === 'closed' && activePanel) {
        closePanel({ focus: false, instant: true });
      }
    }).observe(drawer, { attributes: true, attributeFilter: ['data-animation-state'] });
  });
})();
